import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Download, Search, Loader2, Eye, Upload, History, ArrowRight } from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTh, sortRows, dateValue, type TableSort, type SortKind } from '@/components/ui/sortable-table';
import { apiFetch, apiFetchBlob } from '@/lib/api-client';
import { JvidLink } from '@/components/dmp/DmpLookup';
import { addressLine } from '@/components/dmp/DmpConsolePanel';

// Admin-only « Retour blacklist » (user ask 2026-09-01) — the identity corrections made to addresses
// Proximus has blacklisted, and their state in the workbook Proximus reads on our SharePoint.
//
// The LOCAL table is the source of truth: a row survives someone editing the Excel by hand, and the
// push is a separate, resumable step whose state each row carries. French-only labels, like the other
// admin pages.

interface BlacklistFeedbackRow {
  id: string;
  jvid: number;
  changedAt: string;
  action: string;
  previousStreetName: string | null;
  previousHouseNumber: string | null;
  previousHouseNumberExt: string | null;
  previousBusNumber: string | null;
  newStreetName: string | null;
  newHouseNumber: string | null;
  newHouseNumberExt: string | null;
  newBusNumber: string | null;
  blacklistReason: string | null;
  blacklistFile: string | null;
  actorEmail: string | null;
  /** Le commentaire d'une ligne forcée (17-09) ; null sur une correction ordinaire. */
  comment: string | null;
  createdAt: string;
  syncedAt: string | null;
  syncError: string | null;
  pushedRow: number | null;
}

const ACTION_LABEL: Record<string, string> = {
  ADDRESS_IDENTITY_EDIT: 'Correction d’adresse',
  ADDRESS_BUS_NUMBER_SWAP: 'Échange de boîtes',
  // « Forcer » depuis la console DMP (17-09) : aucune adresse n'a changé — sans ce libellé et le commentaire,
  // la ligne serait indiscernable d'une correction.
  PROXIMUS_REQUEST: 'Ligne forcée (demande Proximus)',
};

const fmt = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

// Le même `addressLine` que la console : dans le DMP, `house_number_extension` porte le numéro COMPLET,
// suffixe compris (« 22 » → « 22 », « 22 » → « 22A »). Concaténer les deux afficherait le n° 43 « 4343 ».
const line = (street: string | null, num: string | null, ext: string | null, bus: string | null) => {
  const head = addressLine(street, num, ext);
  return bus?.trim() ? `${head} / ${bus.trim()}` : head || '—';
};

const COLUMNS: { key: string; label: string; kind: SortKind; value: (r: BlacklistFeedbackRow) => string | number | null }[] = [
  { key: 'jvid', label: 'JVID', kind: 'number', value: (r) => r.jvid },
  { key: 'changedAt', label: 'Modifié le', kind: 'date', value: (r) => dateValue(r.changedAt) },
  { key: 'action', label: 'Action', kind: 'text', value: (r) => ACTION_LABEL[r.action] ?? r.action },
  { key: 'before', label: 'Avant', kind: 'text', value: (r) => line(r.previousStreetName, r.previousHouseNumber, r.previousHouseNumberExt, r.previousBusNumber) },
  { key: 'after', label: 'Après', kind: 'text', value: (r) => line(r.newStreetName, r.newHouseNumber, r.newHouseNumberExt, r.newBusNumber) },
  { key: 'reason', label: 'Motif blacklist', kind: 'text', value: (r) => r.blacklistReason },
  // Trié sur l'ÉTAT lisible, comme le registre JVID : c'est ce que l'admin lit dans la colonne.
  { key: 'push', label: 'Envoi Proximus', kind: 'text', value: (r) => (r.syncedAt ? '1-Envoyé' : r.syncError ? '2-Retenu' : '3-En attente') },
  { key: 'actor', label: 'Par', kind: 'text', value: (r) => r.actorEmail },
];
const COLUMN_VALUE = new Map(COLUMNS.map((c) => [c.key, c.value]));

function PushBadge({ r }: { r: BlacklistFeedbackRow }) {
  if (r.syncedAt) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"
        title={`Écrit dans le fichier Proximus le ${fmt(r.syncedAt)}${r.pushedRow ? ` — ligne ${r.pushedRow}` : ''}`}
      >
        Envoyé
      </span>
    );
  }
  return (
    <span
      className={
        r.syncError
          ? 'inline-flex items-center text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded'
          : 'inline-flex items-center text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded'
      }
      title={r.syncError ?? 'Pas encore envoyé — le prochain cycle le prendra'}
    >
      {r.syncError ? 'Retenu' : 'En attente'}
    </span>
  );
}

interface PushPreview {
  enabled: boolean;
  configured: boolean;
  file: string;
  sitePath: string;
  sheet: string;
  pending: number;
  rows: { jvid: number; changedAt: string; cells: Record<string, string> }[];
}

export function BlacklistFeedback() {
  const [rows, setRows] = useState<BlacklistFeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'export' | 'preview' | 'push' | 'backfill' | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TableSort | null>(null);
  const [preview, setPreview] = useState<PushPreview | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: BlacklistFeedbackRow[] }>('/admin/blacklist-feedback?limit=2000');
      setRows(res.data);
    } catch (err) {
      toast.error((err as Error)?.message || 'Chargement du retour blacklist impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const kept = rows.filter((r) => {
      if (!q) return true;
      return String(r.jvid).includes(q)
        || (r.previousStreetName ?? '').toLowerCase().includes(q)
        || (r.newStreetName ?? '').toLowerCase().includes(q)
        || (r.blacklistReason ?? '').toLowerCase().includes(q)
        || (r.comment ?? '').toLowerCase().includes(q)
        || (r.actorEmail ?? '').toLowerCase().includes(q);
    });
    return sortRows(kept, sort, (r, key) => COLUMN_VALUE.get(key)?.(r) ?? null);
  }, [rows, search, sort]);

  const pending = rows.filter((r) => !r.syncedAt).length;

  async function handleExport() {
    setBusy('export');
    try {
      const blob = await apiFetchBlob('/admin/blacklist-feedback/export.csv');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'blacklist-feedback.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error((err as Error)?.message || 'Export CSV impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handlePreview() {
    setBusy('preview');
    try {
      const res = await apiFetch<{ data: PushPreview }>('/admin/blacklist-feedback/push-preview');
      setPreview(res.data);
    } catch (err) {
      toast.error((err as Error)?.message || 'Aperçu impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handlePush() {
    setBusy('push');
    try {
      const res = await apiFetch<{ data: { planned: number; pushed: number; failed: number; errors: string[] } }>(
        '/admin/blacklist-feedback/push', { method: 'POST' },
      );
      const s = res.data;
      toast.success(`Envoi terminé : ${s.pushed} ligne(s) écrite(s)${s.failed ? `, ${s.failed} en échec` : ''}.`);
      if (s.errors.length) toast.error(s.errors[0]);
      await load();
    } catch (err) {
      toast.error((err as Error)?.message || 'Envoi impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleBackfill() {
    setBusy('backfill');
    try {
      const res = await apiFetch<{ data: { scanned: number; identityChanges: number; blacklisted: number; inserted: number } }>(
        '/admin/blacklist-feedback/backfill', { method: 'POST' },
      );
      const d = res.data;
      toast.success(
        `Rattrapage : ${d.identityChanges} correction(s) d'identité dans le journal, ${d.blacklisted} sur des adresses blacklistées, ${d.inserted} ligne(s) ajoutée(s).`,
      );
      await load();
    } catch (err) {
      toast.error((err as Error)?.message || 'Rattrapage impossible');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Retour blacklist</h2>
          <p className="text-sm text-slate-500">
            Les corrections d’adresse faites sur des JVID blacklistés par Proximus, et leur envoi vers le
            fichier qu’ils consultent ({rows.length} ligne{rows.length > 1 ? 's' : ''}, {pending} en attente d’envoi)
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Rafraîchir
          </Button>
          {/* Le rattrapage est le filet de la règle « on n'enregistre que les blacklistées » : une
              correction faite quand la blacklist n'était pas à jour se retrouve ici après coup. À
              relancer après chaque dépôt d'une nouvelle blacklist. */}
          <Button variant="outline" size="sm" onClick={() => void handleBackfill()} disabled={busy !== null}>
            {busy === 'backfill' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <History className="w-4 h-4 mr-1.5" />}
            Rattraper depuis le journal
          </Button>
          <Button variant="outline" size="sm" onClick={() => void handlePreview()} disabled={busy !== null}>
            {busy === 'preview' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
            Aperçu de l’envoi
          </Button>
          <Button size="sm" onClick={() => void handlePush()} disabled={busy !== null || pending === 0}>
            {busy === 'push' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            Envoyer maintenant
          </Button>
          <Button variant="outline" size="sm" onClick={() => void handleExport()} disabled={busy !== null}>
            {busy === 'export' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Export CSV
          </Button>
        </div>
      </div>

      {/* L'aperçu est un essai à blanc : il montre les cellules exactes, et n'écrit rien. */}
      {preview && (
        <GlassCard className="p-4">
          <p className="text-sm font-bold text-slate-800">Aperçu — rien n’est écrit</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {preview.sitePath} → {preview.file} (feuille « {preview.sheet} ») ·{' '}
            {preview.enabled
              ? <span className="font-semibold text-emerald-700">envoi ACTIVÉ</span>
              : <span className="font-semibold text-amber-700">envoi ÉTEINT — le bouton « Envoyer » répondra 409</span>}
            {!preview.configured && <span className="font-semibold text-rose-700"> · identifiants SharePoint absents</span>}
            {' · '}{preview.pending} ligne(s) en attente
          </p>
          {preview.rows.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-slate-500">
                  <tr>{Object.keys(preview.rows[0].cells).map((h) => <th key={h} className="px-2 py-1 text-left font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={`${r.jvid}-${i}`} className="border-t border-slate-100">
                      {Object.values(r.cells).map((v, j) => <td key={j} className="px-2 py-1 font-mono text-slate-700">{v || '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 text-right">
            <Button size="sm" variant="outline" onClick={() => setPreview(null)}>Fermer l’aperçu</Button>
          </div>
        </GlassCard>
      )}

      <GlassCard className="p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="JVID, rue, motif, commentaire, auteur…"
            className="pl-9 h-9 text-sm"
          />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                {COLUMNS.map((c) => (
                  <SortableTh
                    key={c.key}
                    label={c.label}
                    sortKey={c.key}
                    kind={c.kind}
                    sort={sort}
                    onSort={setSort}
                    className="px-2 py-2 text-left font-semibold"
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}><td colSpan={COLUMNS.length} className="px-2 py-2"><Skeleton className="h-4 w-full" /></td></tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-2 py-6 text-center text-slate-400">
                    Aucune correction sur une adresse blacklistée.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="px-2 py-2"><JvidLink jvid={r.jvid} /></td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmt(r.changedAt)}</td>
                  <td className="px-2 py-2 text-slate-600">
                    {ACTION_LABEL[r.action] ?? r.action}
                    {r.comment && <p className="mt-0.5 text-[11px] italic text-slate-500">« {r.comment} »</p>}
                  </td>
                  <td className="px-2 py-2 font-mono text-rose-700">
                    {line(r.previousStreetName, r.previousHouseNumber, r.previousHouseNumberExt, r.previousBusNumber)}
                  </td>
                  <td className="px-2 py-2 font-mono text-emerald-700">
                    <span className="inline-flex items-center gap-1">
                      <ArrowRight className="w-3 h-3 text-brand-rose" />
                      {line(r.newStreetName, r.newHouseNumber, r.newHouseNumberExt, r.newBusNumber)}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-slate-600" title={r.blacklistFile ?? ''}>{r.blacklistReason || '—'}</td>
                  <td className="px-2 py-2"><PushBadge r={r} /></td>
                  <td className="px-2 py-2 text-slate-500">{r.actorEmail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
